import {
  addDoc,
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../firebase';
import type { LabResultRecord } from '../types/labs';
import type { ObservationRecord } from '../types/observations';
import type {
  BodyIssueCheckInRecord,
  BodyIssueEvidenceCandidate,
  BodyIssueFeedbackNoteRecord,
  BodyIssueLinkRecord,
  BodyIssueRecord,
  BodyRegionKey,
  BodySystemTag,
} from '../types/bodyMapIssue';
import type { BodyIssueFollowupRecord } from '../types/bodyIssueFollowup';
import { listMenstrualCycleEntries } from './menstrualCycleService';
import { listObservationsForUser } from './observationService';

const BODY_MAP_SCHEMA_VERSION = 1;
const BODY_MAP_MODEL_VERSION = '2026-03-body-map-v1';

function formatDate(value?: unknown) {
  if (!value) return null;
  const date = (value as { toDate?: () => Date }).toDate?.() ?? new Date(value as string);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export async function listBodyIssues(userId: string) {
  const snap = await getDocs(
    query(collection(db, 'users', userId, 'body_issues'), orderBy('updatedAt', 'desc')),
  );
  return snap.docs.map((document) => ({ id: document.id, ...document.data() })) as BodyIssueRecord[];
}

export async function getBodyIssue(userId: string, issueId: string) {
  const snap = await getDocs(query(collection(db, 'users', userId, 'body_issues')));
  const match = snap.docs.find((document) => document.id === issueId);
  return match ? ({ id: match.id, ...match.data() } as BodyIssueRecord) : null;
}

export async function createBodyIssue(
  userId: string,
  input: {
    title: string;
    regionKey: BodyRegionKey;
    side: 'left' | 'right' | 'bilateral' | 'central';
    systemTags: BodySystemTag[];
    status: BodyIssueRecord['status'];
    onsetType: BodyIssueRecord['onsetType'];
    startedAtLabel?: string | null;
    severityBaseline?: number | null;
    symptomSummary?: string | null;
    knownContext?: string | null;
  },
) {
  const ref = await addDoc(collection(db, 'users', userId, 'body_issues'), {
    userId,
    title: input.title.trim(),
    regionKey: input.regionKey,
    side: input.side,
    systemTags: input.systemTags,
    status: input.status,
    onsetType: input.onsetType,
    startedAtLabel: input.startedAtLabel?.trim() || null,
    severityBaseline: input.severityBaseline ?? null,
    symptomSummary: input.symptomSummary?.trim() || null,
    knownContext: input.knownContext?.trim() || null,
    linkedConditionLabels: [],
    linkedInterventionLabels: [],
    sourceType: 'member_manual',
    sourceSystem: 'member_app',
    reviewState: 'member_entered',
    schemaVersion: BODY_MAP_SCHEMA_VERSION,
    modelVersion: BODY_MAP_MODEL_VERSION,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updateBodyIssue(
  userId: string,
  issueId: string,
  updates: Partial<BodyIssueRecord>,
) {
  await updateDoc(doc(db, 'users', userId, 'body_issues', issueId), {
    ...updates,
    updatedAt: serverTimestamp(),
  });
}

export async function listBodyIssueCheckIns(userId: string, issueId: string) {
  const snap = await getDocs(
    query(collection(db, 'users', userId, 'body_issues', issueId, 'check_ins'), orderBy('createdAt', 'desc')),
  );
  return snap.docs.map((document) => ({ id: document.id, ...document.data() })) as BodyIssueCheckInRecord[];
}

export async function createBodyIssueCheckIn(
  userId: string,
  issueId: string,
  input: {
    regionKey: BodyRegionKey;
    betterSameWorse: BodyIssueCheckInRecord['betterSameWorse'];
    painScore?: number | null;
    symptomTypes: BodyIssueCheckInRecord['symptomTypes'];
    visibleChange: BodyIssueCheckInRecord['visibleChange'];
    functionImpact: BodyIssueCheckInRecord['functionImpact'];
    freeText?: string | null;
    redFlagTriggered: boolean;
    redFlagReason?: string | null;
    suggestedFollowUp: BodyIssueCheckInRecord['suggestedFollowUp'];
  },
) {
  const followUpDueAt = input.suggestedFollowUp === 'next_day'
    ? new Date(Date.now() + 24 * 60 * 60 * 1000)
    : input.suggestedFollowUp === 'three_days'
      ? new Date(Date.now() + 3 * 24 * 60 * 60 * 1000)
      : input.suggestedFollowUp === 'one_week'
        ? new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
        : null;

  const ref = await addDoc(collection(db, 'users', userId, 'body_issues', issueId, 'check_ins'), {
    userId,
    issueId,
    regionKey: input.regionKey,
    betterSameWorse: input.betterSameWorse,
    painScore: input.painScore ?? null,
    symptomTypes: input.symptomTypes,
    visibleChange: input.visibleChange,
    functionImpact: input.functionImpact,
    freeText: input.freeText?.trim() || null,
    redFlagTriggered: input.redFlagTriggered,
    redFlagReason: input.redFlagReason || null,
    suggestedFollowUp: input.suggestedFollowUp,
    followUpDueAt,
    sourceType: 'member_manual',
    sourceSystem: 'member_app',
    schemaVersion: BODY_MAP_SCHEMA_VERSION,
    modelVersion: BODY_MAP_MODEL_VERSION,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  await updateDoc(doc(db, 'users', userId, 'body_issues', issueId), {
    latestCheckInAt: serverTimestamp(),
    followUpDueAt,
    status: input.redFlagTriggered ? 'escalated' : 'monitoring',
    updatedAt: serverTimestamp(),
  });

  return ref.id;
}

export async function listBodyIssueLinks(userId: string, issueId: string) {
  const snap = await getDocs(query(collection(db, 'users', userId, 'body_issues', issueId, 'links')));
  return snap.docs.map((document) => ({ id: document.id, ...document.data() })) as BodyIssueLinkRecord[];
}

export async function listPendingBodyIssueFollowups(userId: string) {
  const snap = await getDocs(query(collection(db, 'users', userId, 'body_issue_followups'), orderBy('dueAt', 'asc')));
  return snap.docs
    .map((document) => ({ id: document.id, ...document.data() }) as BodyIssueFollowupRecord)
    .filter((item) => item.status === 'pending');
}

export async function listBodyIssueFeedbackNotes(userId: string, issueId: string) {
  const snap = await getDocs(query(collection(db, 'users', userId, 'body_issues', issueId, 'feedback_notes'), orderBy('createdAt', 'desc')));
  return snap.docs.map((document) => ({ id: document.id, ...document.data() })) as BodyIssueFeedbackNoteRecord[];
}

export async function createBodyIssueLink(
  userId: string,
  issueId: string,
  input: Omit<BodyIssueLinkRecord, 'id' | 'userId' | 'issueId' | 'createdAt'>,
) {
  const ref = await addDoc(collection(db, 'users', userId, 'body_issues', issueId, 'links'), {
    userId,
    issueId,
    ...input,
    notes: input.notes?.trim() || null,
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

export async function listBodyIssueEvidenceCandidates(
  userId: string,
  labResults: LabResultRecord[],
): Promise<BodyIssueEvidenceCandidate[]> {
  const [medicalSnap, wearableUploadSnap, cycleEntries, wearableObservations] = await Promise.all([
    getDocs(collection(db, 'users', userId, 'medical_documents')),
    getDocs(collection(db, 'users', userId, 'wearable_data')),
    listMenstrualCycleEntries(userId),
    listObservationsForUser(userId, 'wearable'),
  ]);

  const medicalDocs = medicalSnap.docs.map((document) => ({
    id: document.id,
    label: String(document.data().fileName || 'Medical document'),
    linkType: 'medical_document' as const,
    linkedCollection: `users/${userId}/medical_documents`,
    subtitle: formatDate(document.data().uploadedAt),
  }));

  const wearableUploads = wearableUploadSnap.docs.map((document) => ({
    id: document.id,
    label: String(document.data().fileName || 'Wearable upload'),
    linkType: 'wearable_upload' as const,
    linkedCollection: `users/${userId}/wearable_data`,
    subtitle: formatDate(document.data().uploadedAt),
  }));

  const labCandidates = labResults.map((result) => ({
    id: result.id,
    label: `${result.biomarker}${result.value ? `: ${result.value}${result.unit ? ` ${result.unit}` : ''}` : ''}`,
    linkType: 'lab_result' as const,
    linkedCollection: `users/${userId}/lab_results`,
    subtitle: result.observedAt ? formatDate(result.observedAt) : null,
  }));

  const observationCandidates = wearableObservations.slice(0, 50).map((observation: ObservationRecord) => ({
    id: observation.id,
    label: `${observation.display}${observation.valueNumber != null ? `: ${observation.valueNumber}${observation.unit ? ` ${observation.unit}` : ''}` : ''}`,
    linkType: 'observation' as const,
    linkedCollection: 'observations',
    subtitle: observation.observedAt ? formatDate(observation.observedAt) : null,
  }));

  const cycleCandidates = cycleEntries.map((entry) => ({
    id: entry.id || '',
    label: `Cycle window ${entry.periodStartDate} to ${entry.periodEndDate}`,
    linkType: 'menstrual_cycle' as const,
    linkedCollection: `users/${userId}/menstrual_cycles`,
    subtitle: entry.symptoms || null,
  }));

  return [...medicalDocs, ...wearableUploads, ...labCandidates, ...observationCandidates, ...cycleCandidates];
}
